import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { FileSpreadsheet, Plus, RefreshCw, Search } from 'lucide-react'
// @ts-ignore
import * as XLSX from 'xlsx-js-style'
import { actualizar, enviar, mensajeError, obtener } from '../api/client'
import { alerta } from '../utils/alertas'
import { Modal } from './Modal'
import type { CampoFormulario, ColumnaTabla } from './PaginaCrud'

interface Accion<T> {
  label: string
  endpoint: (fila: T) => string
  body: (fila: T) => object
  metodo?: 'POST' | 'PUT'
}

interface PaginaOperacionesProps<T extends object> {
  titulo: string
  descripcion?: string
  recurso: string
  llave: keyof T & string
  columnas: ColumnaTabla<T>[]
  campos: CampoFormulario<T>[]
  valorInicial: Partial<T>
  acciones: Accion<T>[]
  puedeCrear?: boolean
  filtroTexto?: (fila: T, texto: string) => boolean
  extras?: ReactNode
}

export function PaginaOperaciones<T extends object>({
  titulo,
  descripcion,
  recurso,
  llave,
  columnas,
  campos,
  valorInicial,
  acciones,
  puedeCrear = false,
  filtroTexto,
  extras
}: PaginaOperacionesProps<T>) {
  const [datos, setDatos] = useState<T[]>([])
  const [cargando, setCargando] = useState(true)
  const [busqueda, setBusqueda] = useState('')
  const [orden, setOrden] = useState<{ campo: string | null; direccion: 'asc' | 'desc' }>({ campo: null, direccion: 'asc' })
  const [modalAbierto, setModalAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [formulario, setFormulario] = useState<Partial<T>>(valorInicial)

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      setDatos(await obtener<T[]>(recurso))
    } catch (error) {
      await alerta.error('No se pudo cargar la informacion', mensajeError(error))
    } finally {
      setCargando(false)
    }
  }, [recurso])

  useEffect(() => {
    void cargar()
  }, [cargar])

  const filtrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase()
    let list = [...datos]
    if (texto) {
      if (filtroTexto) list = list.filter((f) => filtroTexto(f, texto))
      else {
        list = list.filter((fila) =>
          Object.values(fila as Record<string, unknown>).some((valor) =>
            String(valor ?? '').toLowerCase().includes(texto)
          )
        )
      }
    }
    if (orden.campo) {
      list.sort((a, b) => {
        const dir = orden.direccion === 'asc' ? 1 : -1
        const col = orden.campo!
        const va = (a as Record<string, unknown>)[col]
        const vb = (b as Record<string, unknown>)[col]
        let res = 0
        if (typeof va === 'number' && typeof vb === 'number') res = va - vb
        else if (typeof va === 'boolean' && typeof vb === 'boolean') res = Number(va) - Number(vb)
        else res = String(va ?? '').localeCompare(String(vb ?? ''), 'es-MX')
        return res * dir
      })
    }
    return list
  }, [datos, busqueda, filtroTexto, orden])

  const abrirNuevo = () => {
    setFormulario(valorInicial)
    setModalAbierto(true)
  }

  const cambiar = (nombre: string, valor: unknown) =>
    setFormulario((prev) => ({ ...prev, [nombre]: valor }) as Partial<T>)

  const guardar = async () => {
    const faltante = campos.find((c) => c.requerido && !String(formulario[c.nombre] ?? '').trim())
    if (faltante) {
      await alerta.advertencia('Datos incompletos', `El campo "${faltante.etiqueta}" es obligatorio.`)
      return
    }

    setGuardando(true)
    try {
      const respuesta = await enviar(recurso, formulario)
      setModalAbierto(false)
      await cargar()
      void alerta.toast(respuesta.mensaje ?? 'Registro guardado')
    } catch (error) {
      await alerta.error('No se pudo guardar', mensajeError(error))
    } finally {
      setGuardando(false)
    }
  }

  const ejecutarAccion = async (a: Accion<T>, fila: T) => {
    try {
      const ruta = a.endpoint(fila)
      const cuerpo = a.body(fila)
      const respuesta = a.metodo === 'PUT'
        ? await actualizar(ruta, cuerpo)
        : await enviar(ruta, cuerpo)
      await cargar()
      void alerta.toast(respuesta.mensaje ?? `${a.label} completado`)
    } catch (error) {
      await alerta.error('No se pudo ejecutar la accion', mensajeError(error))
    }
  }

  const ordenar = (campo: string) => {
    setOrden((prev) =>
      prev.campo === campo ? { campo, direccion: prev.direccion === 'asc' ? 'desc' : 'asc' } : { campo, direccion: 'asc' }
    )
  }
  const indicador = (campo: string) => (orden.campo === campo ? (orden.direccion === 'asc' ? ' ▲' : ' ▼') : '')

  const getValorExcel = (fila: T, c: ColumnaTabla<T>): string | number => {
    if (c.render) {
      const rendered = c.render(fila)
      if (typeof rendered === 'string' || typeof rendered === 'number') return rendered
    }
    const raw = (fila as Record<string, unknown>)[String(c.campo)]
    if (typeof raw === 'boolean') return raw ? 'Si' : 'No'
    if (typeof raw === 'number') return raw
    return String(raw ?? '')
  }

  const exportarExcel = () => {
    if (filtrados.length === 0) {
      void alerta.toast('No hay registros para exportar')
      return
    }

    const aoa: (string | number)[][] = [
      columnas.map((c) => c.titulo),
      ...filtrados.map((fila) => columnas.map((c) => getValorExcel(fila, c)))
    ]

    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!cols'] = columnas.map((c) => ({ wch: c.ancho ? Math.round(c.ancho / 8) : 20 }))
    ws['!autofilter'] = { ref: ws['!ref']! }

    const r = XLSX.utils.decode_range(ws['!ref']!)
    for (let row = r.s.r; row <= r.e.r; row++) {
      for (let col = r.s.c; col <= r.e.c; col++) {
        const ref = XLSX.utils.encode_cell({ r: row, c: col })
        const cell = ws[ref]
        if (!cell) continue

        if (row === 0) {
          cell.s = {
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12, name: 'Arial' },
            fill: { fgColor: { rgb: '0F766E' }, patternType: 'solid' },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: '0F766E' } },
              bottom: { style: 'thin', color: { rgb: '0F766E' } },
              left: { style: 'thin', color: { rgb: '0F766E' } },
              right: { style: 'thin', color: { rgb: '0F766E' } },
            },
          }
        } else {
          const esNumero = typeof cell.v === 'number'
          const fill = row % 2 === 0 ? 'F0FDF4' : 'FFFFFF'
          cell.s = {
            fill: { fgColor: { rgb: fill }, patternType: 'solid' },
            alignment: { vertical: 'center', horizontal: esNumero ? 'right' : 'left' },
            border: {
              top: { style: 'thin', color: { rgb: 'E5E7EB' } },
              bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
              left: { style: 'thin', color: { rgb: 'E5E7EB' } },
              right: { style: 'thin', color: { rgb: 'E5E7EB' } },
            },
          }
        }
      }
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, titulo)

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([new Uint8Array(wbout)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${titulo.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.xlsx`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="anima-entrada space-y-4">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent/80 text-lg font-bold text-white shadow">
            {titulo.charAt(0)}
          </span>
          <div>
            <h2 className="m-0 text-2xl font-bold text-texto">{titulo}</h2>
            {descripcion && <p className="text-sm text-texto-suave">{descripcion}</p>}
          </div>
        </div>
      </div>

      {extras}

      <div className="mb-2 rounded-xl bg-accent/80 p-2 text-white shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70" />
              <input
                placeholder="Buscar..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full rounded-full border border-white/20 bg-white/10 pl-10 pr-4 py-2 text-sm text-white placeholder-white/50 focus:border-white/50 focus:bg-white/15"
              />
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-2 text-sm">
            <span className="text-white/80">{filtrados.length} registros</span>
            <button
              onClick={() => void cargar()}
              title="Recargar"
              className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3 py-1.5 hover:bg-white/10 transition"
            >
              <RefreshCw size={15} className={cargando ? 'anima-pulso' : ''} />
              
            </button>
            <button
              onClick={() => void exportarExcel()}
              className="inline-flex items-evenly gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-accent shadow hover:bg-gray-100 transition"
            >
              <FileSpreadsheet size={17} /> Excel
            </button>
            {puedeCrear && (
              <button
                onClick={abrirNuevo}
                className="inline-flex items-evenly gap-2 rounded-lg bg-white px-2 py-2 text-sm font-semibold text-accent shadow hover:bg-gray-100 transition"
              >
                <Plus size={17} /> Nuevo
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto max-h-[65vh] rounded-xl border border-borde bg-superficie-soft">
        {cargando ? (
          <div className="p-12 text-center text-texto-suave">Cargando...</div>
        ) : filtrados.length === 0 ? (
          <div className="p-12 text-center text-texto-suave">No hay registros para mostrar.</div>
        ) : (
          <table className="w-full min-w-[640px] text-sm">
            <thead className="sticky top-0 bg-gray-100/95 dark:bg-gray-900/90 text-texto-suave">
              <tr>
                {columnas.map((c) => (
                  <th
                    key={String(c.campo)}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none"
                    style={c.ancho ? { width: `${c.ancho}px` } : undefined}
                    onClick={() => ordenar(String(c.campo))}
                  >
                    {c.titulo}{indicador(String(c.campo))}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borde">
              {filtrados.map((fila) => (
                <tr key={String(fila[llave])} className="hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                  {columnas.map((c) => (
                    <td key={String(c.campo)} className="px-4 py-3 text-texto">
                      {c.render ? c.render(fila) : String(fila[c.campo] ?? '')}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {acciones.map((a) => (
                        <button
                          key={a.label}
                          onClick={() => void ejecutarAccion(a, fila)}
                          className="inline-flex items-center gap-1 rounded-lg border border-borde px-3 py-1 text-sm text-texto hover:bg-black/5 dark:hover:bg-white/10 transition"
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {puedeCrear && (
        <Modal
          abierto={modalAbierto}
          titulo={`Nuevo ${titulo.toLowerCase()}`}
          onCerrar={() => setModalAbierto(false)}
          pie={
            <>
              <button
                className="rounded-lg border border-borde px-4 py-2 text-sm font-medium text-texto hover:bg-black/5 dark:hover:bg-white/10 transition"
                onClick={() => setModalAbierto(false)}
              >
                Cancelar
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white shadow hover:brightness-110 disabled:opacity-60 transition"
                onClick={() => void guardar()}
                disabled={guardando}
              >
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </>
          }
        >
          <div className="grid-2">
            {campos.map((campo) => {
              const valor = formulario[campo.nombre]

              return (
                <div className={campo.tipo === 'textarea' ? 'col-span-full campo' : 'campo'} key={campo.nombre}>
                  <label htmlFor={campo.nombre} className="text-tema">
                    {campo.etiqueta} {campo.requerido && <span className="text-danger">*</span>}
                  </label>

                  {campo.tipo === 'select' ? (
                    <select
                      id={campo.nombre}
                      disabled={campo.soloLectura}
                      value={String(valor ?? '')}
                      onChange={(e) => cambiar(campo.nombre, e.target.value)}
                      className="w-full rounded-lg border border-borde bg-superficie-soft px-3 py-2 text-texto focus:border-tema"
                    >
                      <option value="">Selecciona...</option>
                      {campo.opciones?.map((o) => (
                        <option key={String(o.valor)} value={String(o.valor)}>
                          {o.texto}
                        </option>
                      ))}
                    </select>
                  ) : campo.tipo === 'checkbox' ? (
                    <input
                      id={campo.nombre}
                      type="checkbox"
                      checked={Boolean(valor)}
                      disabled={campo.soloLectura}
                      onChange={(e) => cambiar(campo.nombre, e.target.checked)}
                      className="h-5 w-5 accent-tema"
                    />
                  ) : campo.tipo === 'textarea' ? (
                    <textarea
                      id={campo.nombre}
                      disabled={campo.soloLectura}
                      rows={3}
                      value={String(valor ?? '')}
                      onChange={(e) => cambiar(campo.nombre, e.target.value)}
                      className="w-full rounded-lg border border-borde bg-superficie-soft px-3 py-2 text-texto focus:border-tema"
                    />
                  ) : campo.tipo === 'color' ? (
                    <input
                      id={campo.nombre}
                      type="color"
                      disabled={campo.soloLectura}
                      value={String(valor ?? '#000000')}
                      onChange={(e) => cambiar(campo.nombre, e.target.value)}
                      className="w-full rounded-lg border border-borde bg-superficie-soft px-3 py-2 text-texto focus:border-tema"
                    />
                  ) : campo.tipo === 'numero' ? (
                    <input
                      id={campo.nombre}
                      type="number"
                      disabled={campo.soloLectura}
                      value={String(valor ?? '')}
                      onChange={(e) => cambiar(campo.nombre, e.target.value === '' ? undefined : Number(e.target.value))}
                      className="w-full rounded-lg border border-borde bg-superficie-soft px-3 py-2 text-texto focus:border-tema"
                    />
                  ) : campo.tipo === 'hora' ? (
                    <input
                      id={campo.nombre}
                      type="time"
                      disabled={campo.soloLectura}
                      value={String(valor ?? '')}
                      onChange={(e) => cambiar(campo.nombre, e.target.value)}
                      className="w-full rounded-lg border border-borde bg-superficie-soft px-3 py-2 text-texto focus:border-tema"
                    />
                  ) : (
                    <input
                      id={campo.nombre}
                      type="text"
                      disabled={campo.soloLectura}
                      value={String(valor ?? '')}
                      onChange={(e) => cambiar(campo.nombre, e.target.value)}
                      className="w-full rounded-lg border border-borde bg-superficie-soft px-3 py-2 text-texto focus:border-tema"
                    />
                  )}

                  {campo.ayuda && <p className="text-xs text-texto-suave mt-1">{campo.ayuda}</p>}
                </div>
              )
            })}
          </div>
        </Modal>
      )}
    </div>
  )
}
